import { parseUnits } from 'ethers';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

type UseSupplyInputBaseParams = {
    name: string;
    balance: string;
    errorMessage: string;
    decimals: number;
};

export const useInputBase = ({
    name,
    balance,
    errorMessage,
    decimals = 8,
}: UseSupplyInputBaseParams) => {
    const {
        clearErrors,
        trigger,
        watch,
        setError,
        formState: { errors },
    } = useFormContext();
    const amount = watch(name);

    const fieldError = errors[name];
    const isInvalid = !!fieldError;

    const isError = useMemo(() => {
        if (!amount) return null;
        if (Number(amount) > Number(balance)) {
            return { message: errorMessage };
        }

        if (isInvalid && fieldError) {
            return { message: fieldError.message as string };
        }
        return null;
    }, [amount, balance, errorMessage, isInvalid]);

    const handleAmountChange = async (rawInput: string, setValue: (val: string) => void) => {
        let inputValue = rawInput.trim();

        if (inputValue === '') {
            setValue('');
            clearErrors(name);
            return;
        }

        const regex = new RegExp(`^(0|[1-9]\\d*)(\\.\\d{0,${decimals}})?$`);
        if (!regex.test(inputValue)) {
            const [integerPart, decimalPart] = inputValue.split('.');
            if (decimalPart && decimalPart.length > decimals) {
                inputValue = `${integerPart}.${decimalPart.slice(0, decimals)}`;
            }
        }

        try {
            parseUnits(inputValue, decimals);
            setValue(inputValue);
            await trigger(name);
        } catch {
            setError(name, { message: 'Invalid input' });
        }
    };

    const handleMaxClick = (setValue: any, maxBalance: string) => {
        setValue(maxBalance);
        clearErrors(name);
    };

    return { isError, fieldError, handleAmountChange, handleMaxClick };
};
